import type { Definition } from '../../../../schema/frameworks/entry.js';
import type { wagerApiResponse, WagerId } from '../../../../schema/wager/entry.js';
import {
  AuthorizationError,
  type IAuthorizedUseCase,
  type IDefinitionService,
  type IGetWagerUseCase,
  NotFoundError,
  NotInitializedError,
} from '../../../domain/entry.js';

export type IGetWagerController = ReturnType<typeof getWagerController>;

type WagerResponse = Extract<ReturnType<(typeof wagerApiResponse)['parse']>, { data: object }>['data'];

type ControllerArg = {
  wagerId: WagerId;
  product?: Definition['moduleEngine'];
};

/**
 * Controller to get a wager.
 *
 * @param getWagerUseCase Use case for getting a wager
 * @param authorizedUseCase Use case to check user role
 * @param definitionService Definition service to load definition entities
 * @returns Controller function that retrieves wager data
 */
export function getWagerController(
  getWagerUseCase: IGetWagerUseCase,
  authorizedUseCase: IAuthorizedUseCase,
  definitionService: IDefinitionService
) {
  return async function controller({ wagerId, product }: ControllerArg): Promise<WagerResponse> {
    const isAuthorized = await authorizedUseCase.execute({ role: 'Player' });
    if (!isAuthorized) {
      throw new AuthorizationError('User not authorized to get wager');
    }

    const data = await getWagerUseCase.execute({ wagerId });

    if (product) {
      const definition = await definitionService.loadDefinitionAsync(product);
      if (!definition) {
        throw new NotInitializedError(`Unable to load definition for product: ${product}`);
      }

      if (!definition.isProductIncluded(data.coupon.product)) {
        throw new NotFoundError(`Wager ${wagerId} not found for Product: ${product}`);
      }
    }

    return data;
  };
}
